import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Copy, Check, BadgeCheck, ExternalLink, Wallet } from "lucide-react";
import { claimStart, claimVerify, linkWallet, submitAssetClassification } from "@/lib/api";
import { signEvmMessage, hasInjectedEvm } from "@/lib/onchain";
import { toast } from "sonner";

// Free X verification: the owner posts a tweet containing a unique code, then we
// read that public tweet (no API keys) and confirm author + code match.
// After X verification, an optional (recommended) step links an EVM wallet so
// the profile can be edited from any device by signing with it.
export default function ClaimModal({ entity, open, onClose, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [data, setData] = useState(null); // { code, handle, tweet_text }
  const [tweetUrl, setTweetUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [verified, setVerified] = useState(null); // { edit_token } after X check
  const [linking, setLinking] = useState(false);
  const [assetClass, setAssetClass] = useState("crypto");
  const [assetSubtype, setAssetSubtype] = useState("fungible_token");
  const [sectorTags, setSectorTags] = useState("");
  const [supportingUrl, setSupportingUrl] = useState("");
  const [declaring, setDeclaring] = useState(false);

  useEffect(() => {
    if (open && entity) {
      setData(null); setTweetUrl(""); setCopied(false); setLoading(true);
      setVerified(null); setLinking(false);
      claimStart(entity.id)
        .then(setData)
        .catch((e) => toast.error(e.response?.data?.detail || "Could not start verification"))
        .finally(() => setLoading(false));
    }
  }, [open, entity]);

  if (!entity) return null;

  const tweetIntent = data
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(data.tweet_text + "\n" + window.location.href)}`
    : "#";

  const copyCode = async () => {
    await navigator.clipboard.writeText(data.code);
    setCopied(true); toast.success("Code copied"); setTimeout(() => setCopied(false), 1500);
  };

  const verify = async () => {
    if (!tweetUrl.trim()) { toast.error("Paste the link to your tweet"); return; }
    setVerifying(true);
    try {
      const r = await claimVerify(entity.id, tweetUrl.trim());
      toast.success("Verified ✓ — your profile is now claimed!");
      if (hasInjectedEvm()) {
        setVerified(r); // stay open: offer wallet linking
      } else {
        onVerified(r);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const declareClassification = async () => {
    if (!verified?.edit_token) return;
    setDeclaring(true);
    try {
      const result = await submitAssetClassification(entity.id, {
        edit_token: verified.edit_token,
        asset_class: assetClass,
        asset_subtype: assetSubtype,
        sector_tags: sectorTags.split(",").map((t) => t.trim()).filter(Boolean),
        supporting_url: supportingUrl.trim() || null,
      });
      if (result?.applied) toast.success("Classification saved as self-declared");
      else toast.message(result?.reason || "Saved for review. A stronger classification was kept.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save classification");
    } finally {
      setDeclaring(false);
    }
  };

  const linkAndFinish = async () => {
    setLinking(true);
    try {
      const msg = `FLIP profile ownership\nentity: ${entity.id}\n\nSigning links this wallet as the verified owner of this profile on FLIP.`;
      const { address, signature } = await signEvmMessage(msg);
      await linkWallet(entity.id, verified.edit_token, address, signature);
      toast.success("Wallet linked — you can now edit this profile from any device");
      onVerified({ ...verified, owner_wallet: address });
    } catch (e) {
      if (e?.code === 4001) toast.error("Signature rejected in wallet");
      else toast.error(e.response?.data?.detail || e.message || "Wallet link failed");
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !verifying && !linking && onClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border-white/10 bg-[#0a0a0a] p-0" data-testid="claim-modal">
        <DialogTitle className="sr-only">Claim profile</DialogTitle>
        <DialogDescription className="sr-only">Verify your X account and optionally link a wallet to manage this profile.</DialogDescription>
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 pr-12">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-sky-400" />
            <div className="font-display text-lg font-black tracking-tight">
              {verified ? "Link your wallet" : `Verify @${data?.handle || ""}`}
            </div>
          </div>
        </div>

        {verified ? (
          <div className="space-y-4 px-5 py-5" data-testid="claim-link-wallet">
            <div className="flex items-start gap-2 rounded-lg border border-positive/25 bg-positive/[0.05] px-3 py-2 text-[12px] text-positive">
              <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>X account verified. One last step: <b>link your wallet</b> — from then on you can edit this profile from any device just by signing with it.</span>
            </div>
            {entity.category !== "callers" && (
              <div className="space-y-2 rounded-lg border border-white/10 p-3" data-testid="claim-classification">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Optional · self-declared classification</div>
                <p className="text-[11px] text-zinc-500">This stays self-declared until BackOnFlip or an official source verifies it. It does not change Backing or rank.</p>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="claim-asset-class" value={assetClass} onChange={(e) => setAssetClass(e.target.value)} className="rounded border border-white/15 bg-black px-2 py-1.5 text-xs">
                    <option value="crypto">Crypto</option>
                    <option value="stablecoin">Stablecoin</option>
                    <option value="rwa">RWA</option>
                    <option value="liquidity_asset">Liquidity</option>
                  </select>
                  <select data-testid="claim-asset-subtype" value={assetSubtype} onChange={(e) => setAssetSubtype(e.target.value)} className="rounded border border-white/15 bg-black px-2 py-1.5 text-xs">
                    <option value="fungible_token">Fungible token</option>
                    <option value="meme_token">Meme</option>
                    <option value="ai_agent_token">AI agent</option>
                    <option value="utility_token">Utility</option>
                    <option value="stock_token">Stock Token</option>
                    <option value="etf_token">ETF Token</option>
                    <option value="fiat_backed_stablecoin">Fiat-backed stable</option>
                    <option value="unknown">Unspecified</option>
                  </select>
                </div>
                <input data-testid="claim-sector-tags" value={sectorTags} onChange={(e) => setSectorTags(e.target.value)} placeholder="Tags, e.g. ai, agents" className="w-full rounded border border-white/15 bg-black px-2 py-1.5 text-xs" />
                <input data-testid="claim-supporting-url" value={supportingUrl} onChange={(e) => setSupportingUrl(e.target.value)} placeholder="https:// official reference (optional)" className="w-full rounded border border-white/15 bg-black px-2 py-1.5 text-xs" />
                <button type="button" data-testid="claim-save-classification" disabled={declaring} onClick={declareClassification} className="text-[11px] font-bold uppercase tracking-wide text-zinc-300 hover:text-white disabled:opacity-40">
                  {declaring ? "Saving…" : "Save as self-declared"}
                </button>
              </div>
            )}
            <button
              data-testid="claim-link-wallet-button" disabled={linking} onClick={linkAndFinish}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:opacity-30"
            >
              {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} Sign with wallet
            </button>
            <button
              data-testid="claim-link-skip" disabled={linking}
              onClick={() => onVerified(verified)}
              className="w-full text-center text-[11px] font-bold uppercase tracking-wide text-zinc-500 hover:text-zinc-300"
            >
              I'll do it later
            </button>
            <p className="text-center text-[10px] text-zinc-600">Signing is free — it never moves funds.</p>
          </div>
        ) : (
          <>
            {loading || !data ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-white" /></div>
            ) : (
              <div className="space-y-4 px-5 py-5">
                <p className="text-sm text-zinc-400">Prove you own <b className="text-white">@{data.handle}</b> on X by posting a tweet with your unique code. It's free and takes 20 seconds.</p>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Step 1 · Your verification code</div>
                  <button onClick={copyCode} data-testid="claim-copy-code"
                    className="mt-1 flex w-full items-center justify-between rounded-xl border border-white/15 bg-black px-3 py-2.5 hover:border-white/40">
                    <span className="font-mono text-base font-bold text-sky-400">{data.code}</span>
                    {copied ? <Check className="h-4 w-4 text-positive" /> : <Copy className="h-4 w-4 text-zinc-500" />}
                  </button>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Step 2 · Post the tweet</div>
                  <a href={tweetIntent} target="_blank" rel="noreferrer" data-testid="claim-tweet-button"
                    className="mt-1 flex items-center justify-center gap-2 rounded-full bg-sky-500 py-2.5 font-display text-sm font-black uppercase tracking-wide text-white transition-transform hover:bg-sky-400 active:scale-95">
                    Post verification tweet <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Step 3 · Paste the tweet link</div>
                  <input
                    data-testid="claim-tweet-url" value={tweetUrl} onChange={(e) => setTweetUrl(e.target.value)}
                    placeholder="https://x.com/you/status/123…"
                    className="mt-1 w-full rounded-xl border border-white/15 bg-black px-3 py-2.5 font-mono text-xs outline-none focus:border-white/40"
                  />
                </div>

                <button
                  data-testid="claim-verify-button" disabled={verifying || !tweetUrl.trim()} onClick={verify}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:opacity-30"
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />} Verify my account
                </button>
                <p className="text-center text-[10px] text-zinc-600">We only read your public tweet — we never ask for your password or X login.</p>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
